import { Chip } from '@mui/material';
import { cn } from 'cn';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { components, type GroupBase, type MenuListProps } from 'react-select';
import {
  AsyncPaginate,
  type LoadOptions,
  type ShouldLoadMore,
  wrapMenuList,
} from 'react-select-async-paginate';

import { EMPTY_DROPDOWN_OPTION } from '~/constants/select';

import { promiseAllThrottled } from '~/utils/promise';

import { Spinner } from '@vestigas/ui/molecules';

export type Option<T = any> = {
  value: string;
  label: string;
  data: T;
};

export type SelectServerDrivenProps<T> = {
  readonly debounceTime?: number;
  readonly errors?: string;
  readonly filterActive?: boolean | null;
  readonly formatOptionLabel?: (
    option: Option<T>,
    formatOptionLabelMeta: any,
  ) => ReactNode;
  readonly getItemData: (value: string) => Promise<T | undefined>;
  readonly getOptionLabel: (item: T) => string;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isItemSelectionAllowed: () => boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<T>) => boolean;
  readonly loadOptions: LoadOptions<
    Option<T>,
    GroupBase<Option<T>>,
    { page: number; filterActive?: boolean | null }
  >;
  readonly onChange: {
    (value: string[], data: T[]): void;
    (value: string, data: T): void;
  };
  readonly onMultiValueClick?: (data: Option<T>) => void;
  readonly placeholder?: string;
  readonly value: string | string[];
} & ComponentStyling;

const alwaysTrue = () => true;
const hideComponent = () => null;

/**
 * A function used to determine if the select should try to load more options if available.
 * Made it so it will return true if the user is 2/3 of the way down
 * This looks like a good compromise for loading additional options without adding too much delay.
 *
 * @param scrollHeight - The height of the scrollable container
 * @param clientHeight - The height of the visible area of the scrollable container
 * @param scrollTop - The distance of the scrollable container from the top of the page
 * @returns Whether the load more button should be shown
 */
const shouldLoadMore: ShouldLoadMore = (
  scrollHeight,
  clientHeight,
  scrollTop,
) => {
  const bottomBorder = ((scrollHeight - clientHeight) * 2) / 3;

  return bottomBorder < scrollTop;
};

const CustomMenuList = (
  props: MenuListProps & {
    readonly isLoading: boolean;
    readonly options: any[];
  },
) => {
  const { isLoading, options, children } = props;

  return (
    <components.MenuList {...props}>
      {children}
      {isLoading && options.length > 0 ? <Spinner className="py-4" /> : null}
    </components.MenuList>
  );
};

/**
 * SelectServerDriven is a customizable, server-driven select component that supports both single and multi-select modes.
 * It uses react-select-async-paginate for efficient loading of paginated data from a server.
 *
 * @template T - The type of the item data returned by getItemData
 *
 * @param props - The component props
 * @param props.className - Additional CSS class for the select component
 * @param props.debounceTime - Debounce time for search input in milliseconds (default: 300)
 * @param props.formatOptionLabel - Function to format the option label
 * @param props.getItemData - Function to fetch item data for a given value
 * @param props.getOptionLabel - Function to get the label string for an item
 * @param props.isClearable - Whether the select allows clearing the selection (default: true)
 * @param props.isDisabled - Whether the select is disabled (default: false)
 * @param props.isItemSelectionAllowed - Function to determine if item selection is allowed
 * @param props.isMultiSelect - Whether the select allows multiple selections (default: false)
 * @param props.isRemovableValue - Function to determine if a value is removable (default: true)
 * @param props.loadOptions - Function to load options from the server
 * @param props.onChange - Callback function when selection changes
 * @param props.onMultiValueClick - Callback function when a multi-value item (Chip) is clicked
 * @param props.placeholder - Placeholder text for the select (default: 'Element auswählen')
 * @param props.style - Additional inline styles for the select component
 * @param props.value - Current selected value(s)
 *
 * @returns JSX.Element
 */
export const SelectServerDriven = <T,>({
  className,
  debounceTime = 300,
  errors,
  filterActive = true,
  formatOptionLabel,
  getItemData,
  getOptionLabel,
  isClearable = true,
  isDisabled = false,
  isItemSelectionAllowed = alwaysTrue,
  isMultiSelect = false,
  isRemovableValue,
  loadOptions,
  onChange,
  onMultiValueClick,
  placeholder = 'Element auswählen',
  style,
  value,
}: SelectServerDrivenProps<T>): JSX.Element => {
  const { t } = useTranslation('forms');

  const [selectedItems, setSelectedItems] = useState<Array<Option<T>>>([]);

  const [error, setError] = useState<string | undefined>(undefined);

  const getSelectedItems = useCallback(
    async (value, getItemData, getOptionLabel) => {
      try {
        const items = await promiseAllThrottled(
          [value]
            .flat()
            .filter(Boolean) // Only filter out falsy values
            .map(async (value_) => {
              // Special handling for 'empty' option
              if (value_ === EMPTY_DROPDOWN_OPTION) {
                return {
                  data: {
                    id: EMPTY_DROPDOWN_OPTION,
                    name: EMPTY_DROPDOWN_OPTION,
                  },
                  label: EMPTY_DROPDOWN_OPTION,
                  value: EMPTY_DROPDOWN_OPTION,
                };
              }

              const item = await getItemData(value_);

              return item
                ? {
                    data: item,
                    label: getOptionLabel(item),
                    value: value_,
                  }
                : null;
            }),
        );

        setSelectedItems(
          items.filter((item): item is Option<T> => item != null),
        );
      } catch (error) {
        setError('Failed to fetch selected items. Please try again.');
        console.error('Error fetching selected items:', error);
      }
    },
    [JSON.stringify(value), getItemData, getOptionLabel],
  );

  useLayoutEffect(() => {
    if (!value) {
      return;
    }

    void getSelectedItems(value, getItemData, getOptionLabel);
  }, [value, getItemData, getOptionLabel, getSelectedItems]);

  // oxlint-disable-next-line react-x/no-nested-component-definitions -- MultiValue closes over select state and callbacks.
  const MultiValue = ({ data }: { readonly data: Option<T> }) => (
    <Chip
      className="mr-1 first:-ml-1.5"
      label={data.label}
      onClick={
        onMultiValueClick
          ? () => {
              onMultiValueClick(data);
            }
          : undefined
      }
      onDelete={
        typeof isRemovableValue !== 'function' || isRemovableValue?.(data)
          ? () => {
              handleChange(
                selectedItems.filter(({ value }) => value !== data.value),
              );
            }
          : undefined
      }
    />
  );

  const preserveNonRemovableValues = useCallback(
    (nextSelectedItems: Array<Option<T>>) => {
      if (!isMultiSelect || typeof isRemovableValue !== 'function') {
        return nextSelectedItems;
      }

      // Walk the original order: keep locked items and items still selected
      const merged = selectedItems.filter(
        (item) =>
          !isRemovableValue(item) ||
          nextSelectedItems.some(({ value }) => value === item.value),
      );

      // Append newly added items (not previously in selectedItems)
      const brandNew = nextSelectedItems.filter(
        (item) => !selectedItems.some(({ value }) => value === item.value),
      );

      return [...merged, ...brandNew];
    },
    [isMultiSelect, isRemovableValue, selectedItems],
  );

  const handleChange = useCallback(
    (newValue: Option<T> | Array<Option<T>> | null | undefined) => {
      const next = [newValue]
        .flat()
        .filter((item): item is Option<T> => item != null);
      const newSelectedItems = preserveNonRemovableValues(next);

      setSelectedItems(newSelectedItems);

      const newValues = newSelectedItems.map(({ value }) => value);
      const newData = newSelectedItems.map(({ data }) => data);

      onChange(
        isMultiSelect ? newValues : (newValues[0] ?? ''),
        isMultiSelect ? newData : newData[0],
      );
    },
    [onChange, isMultiSelect],
  );

  const handleLoadOptions: typeof loadOptions = async (...arguments_) => {
    try {
      return await loadOptions(...arguments_);
    } catch (error) {
      setError(
        t(($) => $.selectServerDriven.error.loadOptions, {
          ns: 'forms',
        }),
      );
      console.error('Error loading options:', error);

      return {
        hasMore: false,
        options: [],
      };
    }
  };

  const additional = {
    filterActive,
    page: 1,
  };

  const memoizedStyles = useMemo(
    () => ({
      control: (provided: CSSProperties) => ({
        ...provided,
        minHeight: '2.5rem',
      }),
      indicatorsContainer: (provided: CSSProperties) => ({
        ...provided,
        alignItems: 'flex-start',
      }),
      menu: (provided: CSSProperties) => ({
        ...provided,
        margin: 0,
      }),
      menuPortal: (provided: CSSProperties) => ({
        ...provided,
        zIndex: 1500,
      }),
      placeholder: (provided: CSSProperties) => ({
        ...provided,
        color: 'hsl(213 1 60)', // TODO: use CSS variable text lighter once Tailwind is migrated to CSS variables
        fontStyle: 'italic',
      }),
      valueContainer: (provided: CSSProperties) => ({
        ...provided,
        rowGap: '0.25rem',
      }),
      ...style,
    }),
    [style],
  );

  return (
    <>
      <AsyncPaginate<Option<T>, GroupBase<Option<T>>, { page: number }>
        additional={additional}
        className={className}
        classNames={{
          control: () => cn(errors && 'border-tiga-semantic-error'),
        }}
        components={{
          IndicatorSeparator: hideComponent,
          MenuList: wrapMenuList((props) => <CustomMenuList {...props} />),
          MultiValue,
        }}
        debounceTimeout={debounceTime}
        formatOptionLabel={formatOptionLabel}
        isClearable={isClearable}
        isDisabled={isDisabled || !isItemSelectionAllowed()}
        isMulti={isMultiSelect}
        isRemovableValue={isRemovableValue}
        loadingMessage={() => 'Lade Daten...'}
        loadOptions={handleLoadOptions}
        menuPortalTarget={document.body}
        menuShouldScrollIntoView={false}
        noOptionsMessage={() => 'Keine passenden Daten gefunden'}
        onChange={handleChange}
        placeholder={placeholder}
        shouldLoadMore={shouldLoadMore}
        styles={memoizedStyles}
        value={isMultiSelect ? selectedItems : selectedItems[0] || null}
      />
      {error ? <div className="mt-1 text-red-500">{error}</div> : null}
    </>
  );
};
